import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, X, ArrowUpRight, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { v4 as uuidv4 } from "uuid";

const QuickSendModal = ({ 
  isOpen, 
  selectedRecipient, 
  balance, 
  currency = "JMD",
  walletId,
  onClose, 
  onSend 
}) => {
  const [amount, setAmount] = useState("50");
  const [isEditing, setIsEditing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [sendStatus, setSendStatus] = useState(null); // null, 'success', 'error'
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const queryClient = useQueryClient();
  const prevIsOpen = useRef(isOpen);

  // Currency symbols
  const currencySymbols = {
    JMD: "J$",
    USD: "$",
    GBP: "£",
    CAD: "C$"
  };

  const symbol = currencySymbols[currency] || "$";

  // Format currency for display
  const formatBalance = (bal) => {
    return `${symbol}${bal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Parse amount to number
  const numericAmount = parseFloat(amount) || 0;

  // Validation
  const isValidAmount = numericAmount > 0;
  const hasInsufficientFunds = numericAmount > balance;
  const canSend = isValidAmount && !hasInsufficientFunds && !isSending && sendStatus === null;

  // Reset state when modal opens (using ref to track previous state)
  if (isOpen && !prevIsOpen.current) {
    // Modal just opened - reset state synchronously during render
    if (amount !== "50") setAmount("50");
    if (isEditing) setIsEditing(false);
    if (error) setError("");
    if (isSending) setIsSending(false);
    if (sendProgress !== 0) setSendProgress(0);
    if (sendStatus !== null) setSendStatus(null);
  }
  prevIsOpen.current = isOpen;

  // Focus input when editing
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen && !isSending) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    }
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, isSending]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (modalRef.current && !modalRef.current.contains(e.target) && !isSending) {
      onClose();
    }
  }, [onClose, isSending]);

  // Animate progress bar
  const animateProgress = () => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 2;
        setSendProgress(progress);
        if (progress >= 100) {
          clearInterval(interval);
          resolve();
        }
      }, 60); // 3 seconds total (50 steps * 60ms = 3000ms)
    });
  };

  // Handle send
  const handleSend = async () => {
    if (!canSend) return;
    
    setIsSending(true);
    setError("");
    setSendProgress(0);
    
    try {
      // Start progress animation
      const progressPromise = animateProgress();
      
      // Make API call
      const apiPromise = onSend 
        ? onSend(selectedRecipient.id, numericAmount, currency)
        : axios.post(`${API}/transfers`, {
            from_wallet_id: walletId,
            to_user_email: selectedRecipient.email || `${selectedRecipient.name.toLowerCase()}@stelxpay.com`,
            amount: numericAmount,
            currency: currency,
            idempotency_key: uuidv4(),
            note: `Quick send to ${selectedRecipient.name}`
          });
      
      // Wait for both to complete (progress animation takes at least 3s)
      const [, response] = await Promise.all([progressPromise, apiPromise]);
      
      // Success!
      setSendStatus('success');
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      
      // Auto close after showing success
      setTimeout(() => {
        toast.success("Sent!", {
          description: `${symbol}${numericAmount} sent to ${selectedRecipient.name}`
        });
        onClose();
      }, 1500);
      
    } catch (err) {
      setSendProgress(100);
      setSendStatus('error');
      setError(err.response?.data?.detail || "Transfer failed. Please try again.");
      setIsSending(false);
      
      // Reset after showing error
      setTimeout(() => {
        setSendStatus(null);
        setSendProgress(0);
      }, 3000);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setAmount(value);
      setError("");
    }
  };

  const handleAmountBlur = () => {
    setIsEditing(false);
    // Clean up the amount
    if (amount === "" || amount === ".") {
      setAmount("0");
    } else {
      setAmount(parseFloat(amount).toString());
    }
  };

  if (!isOpen || !selectedRecipient) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      data-testid="quick-send-modal-backdrop"
    >
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-md transition-opacity duration-200"
        style={{ animation: "fadeIn 150ms ease-out" }}
      />
      
      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-[380px] bg-white rounded-[24px] shadow-2xl overflow-hidden"
        style={{ 
          animation: "modalIn 200ms ease-out",
          marginBottom: "5vh"
        }}
        data-testid="quick-send-modal"
      >
        {/* Success/Error Overlay */}
        {sendStatus && (
          <div 
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white"
            style={{ animation: "fadeIn 200ms ease-out" }}
          >
            {sendStatus === 'success' ? (
              <>
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4 animate-bounce-in">
                  <Check className="w-10 h-10 text-emerald-500" strokeWidth={3} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Sent!</h3>
                <p className="text-gray-500">
                  {symbol}{numericAmount} to {selectedRecipient.name}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Failed</h3>
                <p className="text-gray-500 text-center px-6 text-sm">{error}</p>
                <button
                  onClick={() => {
                    setSendStatus(null);
                    setSendProgress(0);
                    setError("");
                  }}
                  className="mt-4 px-6 py-2 rounded-full bg-gray-100 text-gray-700 font-medium hover:bg-gray-200"
                >
                  Try Again
                </button>
              </>
            )}
          </div>
        )}

        {/* Header */}
        <div className="px-5 pt-5 pb-2 flex items-center">
          <button
            onClick={onClose}
            disabled={isSending}
            className={`w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center 
              transition-colors ${isSending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
            data-testid="quick-send-back-btn"
          >
            <ChevronLeft className="w-5 h-5 text-gray-900" />
          </button>
          <h2 className="flex-1 text-center text-lg font-semibold text-gray-900 pr-8">
            Quick send to
          </h2>
        </div>

        {/* Recipient */}
        <div className="flex flex-col items-center pt-2 pb-4">
          <div className="w-16 h-16 rounded-full overflow-hidden mb-2 ring-4 ring-purple-100">
            {selectedRecipient.avatar ? (
              <img 
                src={selectedRecipient.avatar} 
                alt={selectedRecipient.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={`w-full h-full flex items-center justify-center text-white text-xl font-semibold
                ${selectedRecipient.color || "bg-gradient-to-br from-purple-400 to-pink-400"}`}>
                {selectedRecipient.initial || selectedRecipient.name?.charAt(0) || "?"}
              </div>
            )}
          </div>
          <p className="text-gray-900 font-medium">{selectedRecipient.name}</p>
        </div>

        {/* Amount Section */}
        <div className="px-5 pb-4">
          {/* Amount Display/Input */}
          <div 
            className="mb-4 cursor-pointer"
            onClick={() => !isSending && setIsEditing(true)}
          >
            {isEditing ? (
              <div className="flex items-baseline">
                <span className="text-4xl font-semibold text-gray-900">{symbol}</span>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={handleAmountChange}
                  onBlur={handleAmountBlur}
                  className="text-4xl font-semibold text-gray-900 bg-transparent border-none 
                    outline-none w-32 focus:ring-0 p-0"
                  data-testid="quick-send-amount-input"
                />
              </div>
            ) : (
              <p 
                className="text-4xl font-semibold text-gray-900"
                data-testid="quick-send-amount-display"
              >
                {symbol}{numericAmount}
              </p>
            )}
          </div>

          {/* Balance Row with Divider */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <span className="text-sm text-purple-500 font-medium">Your balance</span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 font-mono">{formatBalance(balance)}</span>
              {/* Close mini button */}
              <button
                onClick={onClose}
                disabled={isSending}
                className={`w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center
                  transition-colors ${isSending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                data-testid="quick-send-close-mini"
              >
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Error Message (validation only, not send error) */}
          {hasInsufficientFunds && !sendStatus && (
            <p className="text-sm text-red-500 mt-2">
              Insufficient balance
            </p>
          )}
        </div>

        {/* Send Button */}
        <div className="px-5 pb-5">
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={`relative w-full h-14 rounded-full overflow-hidden flex items-center justify-between px-6
              transition-all duration-200 ${
                canSend 
                  ? "bg-gray-900 hover:bg-gray-800 active:scale-[0.98]" 
                  : "bg-gray-200 cursor-not-allowed"
              }`}
            data-testid="quick-send-btn"
          >
            {/* Progress bar background */}
            {isSending && (
              <div 
                className="absolute inset-0 bg-emerald-500 transition-all duration-100 ease-linear"
                style={{ width: `${sendProgress}%` }}
              />
            )}
            
            {/* Button content */}
            <span className={`relative z-10 text-base font-semibold ${
              isSending ? "text-white" : canSend ? "text-white" : "text-gray-400"
            }`}>
              {isSending 
                ? `Sending... ${sendProgress}%` 
                : `Send ${symbol}${numericAmount || 0}`
              }
            </span>
            
            {!isSending && (
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${
                canSend ? "bg-white/20" : "bg-gray-300"
              }`}>
                <ArrowUpRight className={`w-4 h-4 ${canSend ? "text-white" : "text-gray-400"}`} />
              </div>
            )}
            
            {isSending && (
              <div className="relative z-10 w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: scale(0.98) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes bounce-in {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        
        .animate-bounce-in {
          animation: bounce-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QuickSendModal;
